import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  Building2,
  Database,
  Download,
  Monitor,
  Settings,
  Shield,
  Upload,
  Users,
  Wrench,
} from 'lucide-react';
import { Heading } from '../layout';
import { useUIColors } from '../../hooks/useTheme';
import {
  ADMINISTRATION_CATALOG,
  getAdministrationCategory,
} from './administrationCatalog';
import { useAuth } from '../../contexts/AuthContext';
import { filterAdministrationCatalog } from '../../utils/permissions';

const CATEGORY_ICONS = {
  admin: Users,
  company: Building2,
  monitoring: Monitor,
  datos: Database,
};

const ITEM_ICONS = {
  users: Users,
  permissions: Shield,
  maintenance: Wrench,
  general: Settings,
  configuration: Settings,
  activity: Activity,
  sessions: Monitor,
  importacion: Upload,
  exportacion: Download,
};

const AdministrationHubWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const navigate = useNavigate();
  const { categoryKey } = useParams();
  const { user } = useAuth();
  void isDarkMode;

  const filteredCatalog = filterAdministrationCatalog(ADMINISTRATION_CATALOG, user);
  const category = categoryKey
    ? filteredCatalog.find((entry) => entry.key === categoryKey) || null
    : null;
  // Keep getAdministrationCategory available for unknown keys message when not filtered out
  const knownCategory = categoryKey ? getAdministrationCategory(categoryKey) : null;

  const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    border: `1px solid ${ui.border}`,
    borderRadius: '12px',
    background: ui.panel,
    padding: '16px 18px',
    textDecoration: 'none',
    color: ui.text,
    minHeight: '132px',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    cursor: 'pointer',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '14px',
    marginTop: '16px',
  };

  if (categoryKey && !category) {
    return (
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          {knownCategory ? 'Sin permiso para esta sección' : 'Sección no encontrada'}
        </Heading>
        <button
          type="button"
          onClick={() => navigate('/administracion')}
          style={{
            marginTop: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            border: `1px solid ${ui.border}`,
            borderRadius: '8px',
            background: ui.panel,
            color: ui.text,
            padding: '8px 12px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          <ArrowLeft size={14} />
          Volver a Administración
        </button>
      </div>
    );
  }

  if (category) {
    return (
      <div>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: '6px' }}>
          {category.title}
        </Heading>
        <button
          type="button"
          onClick={() => navigate('/administracion')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            border: 'none',
            background: 'transparent',
            color: ui.interactive,
            padding: 0,
            margin: '0 0 8px',
            alignSelf: 'flex-start',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          <ArrowLeft size={14} strokeWidth={2.25} />
          Volver a Administración
        </button>
        <p style={{ margin: 0, color: ui.muted, fontSize: '13px', maxWidth: '640px' }}>
          {category.description}
        </p>
        <div style={gridStyle}>
          {category.items.map((item) => {
            const Icon = ITEM_ICONS[item.key] || Settings;
            return (
              <Link
                key={item.key}
                to={item.href}
                style={cardStyle}
                onMouseEnter={(event) => {
                  event.currentTarget.style.borderColor = ui.interactive;
                  event.currentTarget.style.background = `color-mix(in srgb, ${ui.interactive} 8%, ${ui.panel})`;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.borderColor = ui.border;
                  event.currentTarget.style.background = ui.panel;
                }}
              >
                <span
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '9px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `color-mix(in srgb, ${ui.interactive} 14%, ${ui.panel})`,
                    color: ui.interactive,
                  }}
                >
                  <Icon size={18} />
                </span>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 750, lineHeight: 1.25 }}>{item.title}</div>
                  <div style={{ marginTop: '6px', fontSize: '12px', color: ui.muted, lineHeight: 1.4 }}>
                    {item.description}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Heading level={2} isDarkMode={isDarkMode}>
        Administración
      </Heading>
      <p style={{ margin: '6px 0 0', color: ui.muted, fontSize: '13px', maxWidth: '640px' }}>
        Accede a las secciones del sistema: usuarios, empresa, monitoreo y datos.
      </p>
      <div style={gridStyle}>
        {filteredCatalog.map((entry) => {
          const Icon = CATEGORY_ICONS[entry.key] || Settings;
          return (
            <Link
              key={entry.key}
              to={`/administracion/${entry.key}`}
              style={cardStyle}
              onMouseEnter={(event) => {
                event.currentTarget.style.borderColor = ui.interactive;
                event.currentTarget.style.background = `color-mix(in srgb, ${ui.interactive} 8%, ${ui.panel})`;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.borderColor = ui.border;
                event.currentTarget.style.background = ui.panel;
              }}
            >
              <span
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '9px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `color-mix(in srgb, ${ui.interactive} 14%, ${ui.panel})`,
                  color: ui.interactive,
                }}
              >
                <Icon size={18} />
              </span>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 750, lineHeight: 1.25 }}>{entry.title}</div>
                <div style={{ marginTop: '6px', fontSize: '12px', color: ui.muted, lineHeight: 1.4 }}>
                  {entry.description}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default AdministrationHubWorkspace;
